import applicationDetailSignal, { ApplicationDetailType, } from './ApplicationDetail.signal';
import { Signal } from '@preact/signals-react';
import api from 'api/api';
import alertSignal from 'signals/Alert.signal';
import { Application, ApplicationApprovalChainConfirmation, FundingProgram, LossClaim, } from 'types';
import loaderSignal from 'signals/Loader.signal';
import { PROGRAM_KEYS } from '../Constant/constants';
import userAccountSignal from 'signals/UserAccount.signal';
import { downloadFromUrl } from 'libs/functions/file';
import { uploadFilesToStorage } from 'libs/functions/global.functions';
import {
  handleCapitalAccessChange,
  handleCollateralSupportChange,
  handleLoanGuarantyChange,
  handleLoanParticipationChange,
  SUBMITTED_FOR_APPROVAL_APPLICATION_INPUT_FIELDS,
  validateCapitalAccessApplication,
  validateCollateralSupportApplication,
  validateLoanGuarantyApplication,
  validateLoanParticipationApplication,
} from 'libs/applications';
import { refreshListAndModal } from '../Approvable/Approvable.helpers';
import ledgerSignal from '../Ledger/Ledger.signals';
import { DateTime } from 'luxon';
import $appSettings from 'signals/AppSettings.signal';

export const txApplicationToApplicationDetailsModal = (
  application: Application,
  ventureCapitalist: boolean
): Partial<ApplicationDetailType> => {
  const {
    business,
    fundingProgramMembership,
    applicationConfig,
    formData,
    supportingDocuments,
    edoUnreadDate,
    fundingProgram,
    technicalAssistance,
  } = application;
  ledgerSignal.update({ application });
  // @ts-ignore - Application may have direct lender relationship via lenderId
  const directLender = (application as any).lender;
  const lender = directLender || fundingProgramMembership?.lender;

  // fundingProgram might be on the application directly or through fundingProgramMembership
  const program = fundingProgram || fundingProgramMembership?.fundingProgram;

  const linkedBankAccount = program?.bankAccountProgramAccesses?.find(
    (access) => access?.bankAccount?.accountId === lender?.accountId
  );

  return {
    id: application.id,
    status: application.status,
    watchListStatus:
      application.watchables?.length
        ? application.watchables[0].status
        : null,
    ventureCapitalist,
    disbursementDate: application.disbursementDate,
    disbursementAmount: application.disbursementAmount,
    firstRepaymentDate: application.disbursements?.length
      ? application.loan?.firstRepaymentDate
      : null,
    calcapLoanNumber: application.calcapLoanNumber || null,
    lenderLoanNumber: (application as unknown as { lenderLoanNumber?: string | null }).lenderLoanNumber ?? null,
    disbursements: application.disbursements,
    disbursementConfig: application.disbursementConfig || {},
    approvedAmount: application.approvedAmount,
    fundingProgramStats: program?.stats,
    fundingProgram: program,
    business: {
      id: business?.id,
      name:
        (applicationConfig?.isUsingOcr
          ? formData?.borrowerinformation_business_name ||
            formData?.borrowerInformation_businessName
          : undefined) ||
        business?.name ||
        formData?.borrowerinformation_business_name ||
        formData?.borrowerInformation_businessName ||
        formData?.businessInformation_businessName ||
        undefined,
      contactName:
        business?.contactName ||
        formData?.borrowerInformation_contactName ||
        formData?.businessInformation_contactName ||
        undefined,
      email:
        business?.contactEmail ||
        formData?.borrowerInformation_email ||
        formData?.businessInformation_email ||
        formData?.businessInformation_emailAddress ||
        undefined,
      phoneNumber:
        business?.phoneNumber ||
        formData?.borrowerInformation_contactPhone ||
        formData?.businessInformation_contactPhone ||
        formData?.businessInformation_phone ||
        undefined,
    },
    lender: lender
      ? {
        id: lender.id,
        name: lender.name ?? null,
        contactName: lender.contactName ?? null,
        email: lender.email ?? null,
        phoneNumber: lender.phoneNumber ?? null,
        otherOrganizationId: lender.otherOrganizationId ?? null,
      }
      : null,
    notes: application.notes,
    documents: (application.supportingDocuments || []).sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    ),
    watchableId:
      application.watchables?.find(
        (w) =>
          w.applicationId === application.id &&
          userAccountSignal.value.userData.id
      )?.id || null,
    modelType: 'application',
    uploadDocumentType: 'applicationDocuments',
    lenderUnread: application.lenderUnread,
    edoUnread: application.edoUnread,
    newWatchListStatus:
      application.watchables?.length
        ? application.watchables[0].status ?? ''
        : '',
    applicationConfig,
    formData,
    approvalAttachments: (supportingDocuments || []).filter(
      (ad) => ad.approvalAttachment
    ),
    approvalChainTemplate:
      application.approvalChainConfig?.approvalChainTemplate,
    approvalChainTemplates: [
      ...(program?.approvalChainTemplates || []).sort(
        (a, b) => b.id - a.id
      ),
    ],
    approvalChainConfirmations: application.approvalChainConfirmations || [],
    requestedAmount: application.askedAmount,
    submittedByEdo: application.submittedByEdo,
    approvedOnCommit: application.approvedOnCommit,
    approvalChainConfig: application.approvalChainConfig,
    createdDate: application.createdDate,
    updatedDate: application.updatedDate,
    edoUnreadDate,
    hasLinkedBankAccount: !!linkedBankAccount,
    usesCaatPayment: program?.platform?.caatForPaymentReconciliation,
    technicalAssistance,
    // Loss claims for this application (if present on the API response)
    lossClaims: ((application as unknown) as { lossClaims?: LossClaim[] }).lossClaims || [],
    // SEDI business flag from application
    sediBusiness: ((application as unknown) as { sediBusiness?: boolean | null }).sediBusiness ?? null,
    // Comments (Comment table) for Status tab Notes section
    applicationComments:
      ((application as unknown) as {
        comments?: Array<{
          id: number;
          applicationId: number | null;
          commentType: string | null;
          commentTypeCode: string | null;
          comments: string | null;
          createdDate: string;
          updatedDate: string;
          createdBy: { id: string; displayName: string; firstName: string; lastName: string } | null;
        }>
      }).comments ?? [],
  };
};

/** Header / Claims read-only loan row: Application CalCAP loan #, lender loan #, Lender CalCAP ID. */
export const syncApplicationDetailLoanDisplayFromDetailsPayload = (
  details: Record<string, unknown> | null | undefined
): void => {
  if (!details) return;
  const lrNested =
    (details.lender as Record<string, unknown> | undefined) ??
    (
      (
        details.fundingProgramMembership as
        | { lender?: Record<string, unknown> }
        | undefined
      )?.lender
    );
  const prev = applicationDetailSignal.value.lender;
  applicationDetailSignal.update({
    calcapLoanNumber: (details.calcapLoanNumber as string | null | undefined) ?? null,
    lenderLoanNumber: (details.lenderLoanNumber as string | null | undefined) ?? null,
    ...(lrNested != null && typeof lrNested === 'object' && lrNested.id != null
      ? {
        lender: {
          id: lrNested.id as number,
          name: (lrNested.name as string | null | undefined) ?? prev?.name ?? null,
          contactName:
            (lrNested.contactName as string | null | undefined) ??
            prev?.contactName ??
            null,
          email: (lrNested.email as string | null | undefined) ?? prev?.email ?? null,
          phoneNumber:
            (lrNested.phoneNumber as string | null | undefined) ??
            prev?.phoneNumber ??
            null,
          otherOrganizationId:
            lrNested.otherOrganizationId != null &&
              String(lrNested.otherOrganizationId).trim() !== ''
              ? String(lrNested.otherOrganizationId).trim()
              : null,
        },
      }
      : {}),
  });
};

export const showApplicationDetailsModal = (
  payload: ApplicationDetailType
): Signal<ApplicationDetailType> =>
  applicationDetailSignal.update({
    ...payload,
    visible: true,
  });

export const hideApplicationDetailsModal = (): void =>
  applicationDetailSignal.reset();

export const handleWatchListStatusDropdownChange = (
  e: React.ChangeEvent<HTMLSelectElement>
): Signal<ApplicationDetailType> => {
  const { value } = e.target;

  applicationDetailSignal.update({
    formErrors: {
      ...applicationDetailSignal.value.formErrors,
      watchListStatusInvalid: false,
    },
  });
  return applicationDetailSignal.update({ newWatchListStatus: value });
};

const validateApplication = (): boolean => true;

const validateApprovedInputFields = () => {
  const { fundingProgramStats, fundingProgram, approvedInputFields } =
    applicationDetailSignal.value;

  const {
    referenceProgram: { key },
    programConfig,
  } = fundingProgram as FundingProgram;

  let validationFn;

  switch (key) {
    case PROGRAM_KEYS.loanParticipation:
    case PROGRAM_KEYS.microLoan:
      validationFn = validateLoanParticipationApplication;
      break;
    case PROGRAM_KEYS.collateralSupport:
      validationFn = validateCollateralSupportApplication;
      break;
    case PROGRAM_KEYS.capitalAccess:
      validationFn = validateCapitalAccessApplication;
      break;
    case PROGRAM_KEYS.loanGuaranty:
      validationFn = validateLoanGuarantyApplication;
      break;
    default:
      alertSignal.update({
        type: 'notification',
        message: `Did not find submit for approval configuration for program key ${key}`,
      });
      return false;
  }

  const errors = validationFn(
    approvedInputFields,
    fundingProgramStats!.remaining,
    programConfig
  );

  if (Object.keys(errors).length > 0) {
    applicationDetailSignal.update({
      approvedInputFieldsErrors: { ...errors },
    });
    return false;
  } else {
    applicationDetailSignal.update({
      approvedInputFieldsErrors: {},
    });
    return true;
  }
};

export const updateApplicationStatus = async (): Promise<
  Signal<unknown> | boolean
> => {
  const {
    newApplicationStatus,
    id,
    approvedInputFields,
    approvalChainTemplates,
    status,
    approvalChainConfig,
  } = applicationDetailSignal.value;
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  const isRetroactiveApprovedWithoutChain =
    status === APPLICATION_STATUS?.approved && !approvalChainConfig;
  const isSubmitForApproval =
    newApplicationStatus === APPLICATION_STATUS?.submittedForApproval;

  // Jul 23: slim Configure Approval — fund/LLR/date inputs not required to submit for approval.
  if (isRetroactiveApprovedWithoutChain) {
    const validApproved = validateApprovedInputFields();
    if (!validApproved) {
      return false;
    }
  } else if (!isSubmitForApproval) {
    const valid = validateApplication();
    if (!valid) {
      return false;
    }
  }

  loaderSignal.update({
    isContentLoading: true,
    message: 'Updating application status...',
  });

  const { borrowerEquity, approvedAmount, disbursementDate } =
    approvedInputFields;

  let data: Record<string, any> = {
    status: newApplicationStatus,
  };

  if (isRetroactiveApprovedWithoutChain || isSubmitForApproval) {
    const hasApprovalChainTemplate = approvalChainTemplates.length > 0;
    const { approvalChainForm } = applicationDetailSignal.value;

    if (isRetroactiveApprovedWithoutChain) {
      const approvedInputFields =
        applicationDetailSignal.value.approvedInputFields;

      const cleanDisbursementConfig = Object.entries(approvedInputFields).reduce(
        (acc, [key, value]) => {
          if (value instanceof Date) {
            return { ...acc, [key]: value.toISOString().split('T')[0] };
          } else if (isNaN(Number(value))) {
            return { ...acc, [key]: value };
          } else {
            return { ...acc, [key]: Number(value) };
          }
        },
        {}
      );

      data = {
        ...data,
        borrowerEquity: Number(borrowerEquity) || 0,
        approvedAmount: Number(approvedAmount),
        disbursementDate:
          typeof disbursementDate === 'string'
            ? disbursementDate
            : (disbursementDate as Date)?.toISOString?.()?.split('T')[0],
        disbursementConfig: {
          ...cleanDisbursementConfig,
        },
      };
    }

    data = {
      ...data,
      ...(hasApprovalChainTemplate && {
        approvalChainConfig: {
          approvalChainTemplate: {
            id: approvalChainTemplates[0].id,
          },
          config: {
            keyDates: Object.entries(approvalChainForm.keyDates).reduce(
              (acc, [key, date]) => ({
                ...acc,
                [key]: DateTime.fromJSDate(date).toISODate(),
              }),
              {}
            ),
            skippedSteps: approvalChainForm.skippedSteps,
            fallbackToAlternatesSteps:
              approvalChainForm.fallbackToAlternatesSteps,
          },
        },
      }),
    };

    if (isRetroactiveApprovedWithoutChain) {
      delete data.status;
      data.action = 'retroactiveApproval';
    }
  }

  try {
    await api.patch({
      path: '/applications',
      body: {
        where: {
          id,
        },
        data,
      },
    });

    await refreshListAndModal(
      id!,
      applicationDetailSignal.value.modelType,
      applicationDetailSignal.value.ventureCapitalist
    );
    applicationDetailSignal.update({ confirmStatusVisible: false });
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated status.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

const getWatchableType = (modelType: ApplicationDetailType['modelType']) => {
  const { WATCHABLE_TYPES } = $appSettings.value.constants;
  if (modelType === 'application') {
    return WATCHABLE_TYPES?.application;
  }

  return WATCHABLE_TYPES?.programRequest;
};

export const createOrUpdateApplicationToWatchlist = async (): Promise<
  Signal<unknown> | undefined
> => {
  const { newWatchListStatus, modelType, formErrors } =
    applicationDetailSignal.value;
  const isApplication = modelType === 'application';
  if (!newWatchListStatus && isApplication) {
    applicationDetailSignal.update({
      formErrors: {
        ...formErrors,
        watchListStatusInvalid: true,
      },
    });
    return;
  }

  try {
    loaderSignal.update({
      isContentLoading: true,
      message: 'Updating watchlist...',
    });
    const { id, ventureCapitalist } = applicationDetailSignal.value;
    await api.put({
      path: '/watchables',
      body: {
        ...(isApplication
          ? { applicationId: id, status: newWatchListStatus }
          : { programRequestId: id }),
        watchableType: getWatchableType(modelType),
      },
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    await refreshListAndModal(id!, modelType, ventureCapitalist);
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated watchlist',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const removeApplicationFromWatchlist = async (
  watchableId: number
): Promise<Signal<unknown>> => {
  loaderSignal.update({
    isContentLoading: true,
    message: 'Removing application from watchlist...',
  });

  try {
    const applicationId = applicationDetailSignal.value.id;
    await api.delete({
      path: '/watchables',
      body: {
        where: {
          id: watchableId,
        },
      },
    });
    await refreshListAndModal(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      applicationId!,
      applicationDetailSignal.value.modelType,
      applicationDetailSignal.value.ventureCapitalist
    );
    applicationDetailSignal.update({
      newWatchListStatus: '',
    });
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully removed from Watchlist',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleApprovedInputFieldChange = (
  e: React.ChangeEvent<HTMLInputElement>
): Signal<unknown> | boolean => {
  const { name } = e.target || e;
  const { value }: { value: string | number } = e.target || e;

  const { fundingProgram } = applicationDetailSignal.value;

  const {
    referenceProgram: { key },
  } = fundingProgram as FundingProgram;

  let changeFn = null;

  switch (key) {
    case PROGRAM_KEYS.loanParticipation:
    case PROGRAM_KEYS.microLoan:
      changeFn = handleLoanParticipationChange;
      break;
    case PROGRAM_KEYS.collateralSupport:
      changeFn = handleCollateralSupportChange;
      break;
    case PROGRAM_KEYS.capitalAccess:
      changeFn = handleCapitalAccessChange;
      break;
    case PROGRAM_KEYS.loanGuaranty:
      changeFn = handleLoanGuarantyChange;
      break;

    default:
      return alertSignal.update({
        type: 'notification',
        message: `Did not find submit for approval configuration for program key ${key}`,
      });
  }

  applicationDetailSignal.update({
    approvedInputFields: changeFn(
      name,
      value,
      applicationDetailSignal.value.approvedInputFields
    ),
    approvedInputFieldsDirty: {
      ...applicationDetailSignal.value.approvedInputFieldsDirty,
      [name]: true,
    },
  });

  return validateApprovedInputFields();
};

export const handleApprovalChainConfirmation = async (
  confirmation: ApplicationApprovalChainConfirmation,
  note: string,
  file: File | undefined,
  confirmedDate: Date | undefined = undefined
): Promise<Signal<unknown>> => {
  const { id, status, submittedByEdo } = applicationDetailSignal.value;
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  if (
    !confirmedDate &&
    submittedByEdo &&
    status === APPLICATION_STATUS?.approved
  ) {
    return alertSignal.update({
      type: 'notification',
      message: 'A confirmation date is needed.',
    });
  }

  if (note === '') {
    return alertSignal.update({
      type: 'notification',
      message: 'A note is needed.',
    });
  }

  // if (!file) {
  //   return alertSignal.update({
  //     type: 'notification',
  //     message: 'An attachment is needed.',
  //   });
  // }
  loaderSignal.update({
    isContentLoading: true,
    message: 'Submitting confirmation...',
  });

  let signedUrls = [];

  if (file) {
    signedUrls = await uploadFilesToStorage(
      [file],
      'approvalChainConfirmationAttachment'
    );
  }

  try {
    const body = {
      data: {
        confirmedDate,
        confirmationBundle: {
          attachments:
            signedUrls.length > 0
              ? [
                {
                  fileType: file!.type,
                  filePath: signedUrls[0],
                },
              ]
              : [],
          note,
        },
        action: 'assert',
      },
    };

    await api.patch({
      path: `/applications/${id}/confirmations/${confirmation.uuid}`,
      body,
    });
    await refreshListAndModal(
      applicationDetailSignal.value.id!,
      applicationDetailSignal.value.modelType,
      applicationDetailSignal.value.ventureCapitalist
    );
    applicationDetailSignal.update({
      approvalAttachmentsToUpload: [],
    });
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully approved application.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleApprovalChainTemplateChange = (
  skippedSteps: number[],
  fallbackToAlternatesSteps: Record<string, number[]>,
  keyDates: Record<string, Date>
): Signal<unknown> =>
  applicationDetailSignal.update({
    approvalChainForm: {
      skippedSteps,
      fallbackToAlternatesSteps,
      keyDates,
    },
  });

export const downloadApplicationPackage = async (): Promise<unknown> => {
  loaderSignal.update({
    isContentLoading: true,
    message: 'Downloading application package...',
  });
  try {
    const body = {
      where: {
        applicationId: applicationDetailSignal.value.id,
      },
      data: {
        status,
      },
    };

    const url = await api.put({
      path: `/applicationPackages`,
      body,
    });

    await downloadFromUrl(
      url as string,
      `application-${applicationDetailSignal.value.id}-package.pdf`
    );

    return undefined;
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: `An unexpected error occurred when creating the application package. ${error.message}`,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const mapOcrValuesToConfirmStatusInputValues = (): void => {
  const { formData, fundingProgram } = applicationDetailSignal.value;
  const {
    referenceProgram: { key },
  } = fundingProgram as FundingProgram;
  const approvedInputFields = {};

  let ocrLoanAmountKey = '';
  switch (key) {
    case PROGRAM_KEYS.microLoan:
    case PROGRAM_KEYS.capitalAccess:
      ocrLoanAmountKey = 'loanInformation_totalLoanAmount';
      break;
    case PROGRAM_KEYS.loanGuaranty:
      ocrLoanAmountKey = 'loanInformation_loanAmount';
      break;
    default:
      break;
  }

  // @ts-ignore
  SUBMITTED_FOR_APPROVAL_APPLICATION_INPUT_FIELDS[key].forEach((field) => {
    if (field.name === 'amountOfCollateralAvailable') {
      // @ts-ignore
      approvedInputFields[field.name] =
        formData[field.ocrFieldKey] && formData[field.ocrFieldKey][1]?.Value
          ? Number(formData[field.ocrFieldKey][1]?.Value.replace(/\D+/g, ''))
          : 0;
    } else if (field.name === 'requiredCollateralAmount') {
      // @ts-ignore
      approvedInputFields[field.name] =
        formData[field.ocrFieldKey] && formData[field.ocrFieldKey][2]?.Value
          ? Number(formData[field.ocrFieldKey][2]?.Value.replace(/\D+/g, ''))
          : 0;
    } else if (field.ocrFieldKey) {
      // @ts-ignore
      approvedInputFields[field.name] = formData[field.ocrFieldKey].value
        ? formData[field.ocrFieldKey].value
        : 0;
    }
  });

  if (key === PROGRAM_KEYS.collateralSupport) {
    // @ts-ignore
    approvedInputFields.approvedAmount =
      formData['collateralAnalysis_collateralAnalysisTableB'] &&
        formData['collateralAnalysis_collateralAnalysisTableB'][0]?.Value
        ? Number(
          formData[
            'collateralAnalysis_collateralAnalysisTableB'
          ][0].Value.replace(/\D+/g, '')
        )
        : '';
    if (
      // @ts-ignore
      approvedInputFields.approvedAmount &&
      // @ts-ignore
      approvedInputFields.approvedAmount > 0
    ) {
      // @ts-ignore
      approvedInputFields.percentageCoverageCollateralLenderLoanApproval =
        // @ts-ignore
        approvedInputFields.requiredCollateralAmount /
        // @ts-ignore
        approvedInputFields.approvedAmount;
    }
    // @ts-ignore
    approvedInputFields.amountOfCollateralSupportRequested =
      // @ts-ignore
      approvedInputFields.requiredCollateralAmount -
      // @ts-ignore
      approvedInputFields.amountOfCollateralAvailable;
  } else {
    // @ts-ignore
    approvedInputFields.approvedAmount = formData[ocrLoanAmountKey]
      ? Number(formData[ocrLoanAmountKey].replace(/\D+/g, ''))
      : 0;
  }

  applicationDetailSignal.update({ approvedInputFields });
};

const FETCH_APPLICATION_INCLUDE = {
  watchables: true,
  business: {
    include: {
      account: true,
    },
  },
  lender: true,
  fundingProgram: {
    include: {
      stats: true,
      bankAccountProgramAccesses: {
        include: {
          bankAccount: true,
        },
      },
      supportingDocuments: true,
      referenceProgram: {
        include: {
          programType: true,
        },
      },
      platform: true,
      approvalChainTemplates: {
        include: {
          steps: {
            include: {
              primaryApprover: true,
              alternateApprovers: {
                include: {
                  user: true,
                },
              },
            },
          },
        },
      },
    },
  },
  fundingProgramMembership: {
    include: {
      lender: true,
      fundingProgram: {
        include: {
          stats: true,
          platform: true,
          edo: true,
          referenceProgram: {
            include: {
              programType: true,
            },
          },
          supportingDocuments: true,
          approvalChainTemplates: {
            include: {
              steps: {
                include: {
                  primaryApprover: true,
                  alternateApprovers: {
                    include: {
                      user: true,
                    },
                  },
                },
              },
            },
          },
          bankAccountProgramAccesses: {
            include: {
              bankAccount: true,
            },
          },
        },
      },
    },
  },
  disbursements: {
    include: {
      reimbursements: {
        include: {
          transactions: {
            include: {
              creator: true,
            },
            orderBy: { createdDate: 'asc' as const },
          },
        },
      },
      originBankAccount: true,
      destinationBankAccount: true,
      transactions: {
        include: {
          creator: true,
        },
        orderBy: { createdDate: 'asc' as const },
      },
    },
  },
  lossClaims: true,
  loan: {
    include: {
      payments: true,
      fees: true,
    },
  },
  technicalAssistance: true,
  comments: {
    include: {
      createdBy: {
        select: {
          id: true,
          displayName: true,
          firstName: true,
          lastName: true,
        },
      },
    },
  },
};

export const fetchApplication = async (
  id: string | number
): Promise<Signal<unknown>> => {
  try {
    loaderSignal.update({
      isContentLoading: true,
      message: 'Loading application...',
    });
    const application = await api.get({
      path: '/applications',
      options: {
        where: {
          id,
        },
        include: FETCH_APPLICATION_INCLUDE,
      },
    });

    const userData = await api.get({
      path: '/users/current',
      options: undefined,
    }, 3)


    userAccountSignal.update({ userData });

    const base = txApplicationToApplicationDetailsModal(application, false);
    let processingBatchMemberships: ApplicationDetailType['processingBatchMemberships'] =
      [];
    if (
      userData?.account?.portalType ===
      $appSettings.value.constants.PORTAL_TYPES.edo
    ) {
      const opts = await fetchLoanTrackingOptions(Number(id));
      processingBatchMemberships = opts.processingBatchMemberships;
    } else {
      processingBatchMemberships = [];
    }

    return applicationDetailSignal.update({
      ...base,
      processingBatchMemberships,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: `An unexpected error occurred when loading the application. ${error.message}`,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const fetchAndSetApprovalChainUsers = async (): Promise<
  Signal<unknown>
> => {
  try {
    const approvalChainConfirmations =
      applicationDetailSignal.value.approvalChainConfirmations;
    const approverIds = approvalChainConfirmations
      .map((acc) => acc.approverIds)
      .flatMap((id) => id)
      .filter(Boolean);

    if (approverIds.length === 0) {
      return applicationDetailSignal.update({ approvalChainUsers: [] });
    }

    const approvalChainUsers = await api.get({
      path: '/users',
      options: {
        where: {
          id: { in: approverIds },
        },
      },
    });

    return applicationDetailSignal.update({
      approvalChainUsers,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: `An unexpected error occurred when loading the approval chain users. ${error.message}`,
    });
  }
};

export const fetchApplicationBlockchain = async (): Promise<
  Signal<unknown>
> => {
  try {
    const approvalChainConfirmations =
      applicationDetailSignal.value.approvalChainConfirmations;
    const { id } = applicationDetailSignal.value;

    const blockchain = await api.get({
      path: `/applications/${id}/chainblocks`,
      options: {
        where: {
          id: {
            in: approvalChainConfirmations
              .map((acc) => acc.approverIds)
              .flatMap((id) => id),
          },
        },
      },
    });

    return applicationDetailSignal.update({
      blockchain,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: `An unexpected error occurred when loading the application blockchain. ${error.message}`,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleUpdateDisbursementConfig = async (): Promise<
  Signal<unknown> | boolean
> => {
  const { approvedInputFields, id } = applicationDetailSignal.value;
  const { borrowerEquity, approvedAmount, disbursementDate } =
    approvedInputFields;

  const cleanDisbursementConfig = Object.entries(approvedInputFields).reduce(
    (acc, [key, value]) => {
      if (value instanceof Date) {
        return { ...acc, [key]: value.toISOString().split('T')[0] };
      } else if (isNaN(Number(value))) {
        return { ...acc, [key]: value };
      } else {
        return { ...acc, [key]: Number(value) };
      }
    },
    {}
  );
  try {
    loaderSignal.update({ isContentLoading: true });

    await api.patch({
      path: '/applications',
      body: {
        where: {
          id,
        },
        data: {
          borrowerEquity: Number(borrowerEquity) || 0,
          approvedAmount: Number(approvedAmount),
          disbursementDate:
            typeof disbursementDate === 'string'
              ? disbursementDate
              : (disbursementDate as Date)?.toISOString?.()?.split('T')[0],
          disbursementConfig: {
            ...cleanDisbursementConfig,
          },
          action: 'updateDisbursementConfig',
        },
      },
    });

    await refreshListAndModal(
      id!,
      applicationDetailSignal.value.modelType,
      applicationDetailSignal.value.ventureCapitalist
    );
    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated disbursement information.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleRevokeApproval = async (
  confirmation: ApplicationApprovalChainConfirmation
): Promise<Signal<unknown> | boolean> => {
  const { id } = applicationDetailSignal.value;

  try {
    loaderSignal.update({ isContentLoading: true });
    await api.patch({
      path: `/applications/${id}/confirmations/${confirmation.uuid}`,
      body: {
        data: {
          action: 'revoke',
        },
      },
    });

    await refreshListAndModal(
      id!,
      applicationDetailSignal.value.modelType,
      applicationDetailSignal.value.ventureCapitalist
    );

    applicationDetailSignal.update({ revokeApprovalChainApprovalVisible: false });

    return alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully revoked approval.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const fetchRawOcrData = async (): Promise<
  Signal<unknown> | undefined
> => {
  const { id } = applicationDetailSignal.value;

  if (!id) {
    return applicationDetailSignal;
  }

  try {
    const rawOcr = await api.get({
      path: `/applications/${id}/rawOcr`,
      options: {}
    });

    if (rawOcr) {
      return applicationDetailSignal.update({
        rawOcr: rawOcr.data,
      });
    }
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      // @ts-ignore
      message: error.message,
    });
  }
};

// Events helper functions
export type LoanTrackingProcessingBatchMembership = {
  memberId: number;
  addedToBatchAt: string;
  batch: {
    id: number;
    name: string;
    status: string;
    fundingProgramName: string | null;
  };
};

export const fetchLoanTrackingOptions = async (
  applicationId: number
): Promise<{
  allowedEventTypes: string[];
  allowedEventStatuses: string[];
  canAdd: boolean;
  canEdit: boolean;
  canRemove: boolean;
  processingBatchMemberships: LoanTrackingProcessingBatchMembership[];
}> => {
  const empty = {
    allowedEventTypes: [] as string[],
    allowedEventStatuses: [] as string[],
    canAdd: false,
    canEdit: false,
    canRemove: false,
    processingBatchMemberships: [] as LoanTrackingProcessingBatchMembership[],
  };
  try {
    const response = await api.get({
      path: `/v2/applications/${applicationId}/loan-tracking-options`,
      options: {},
    });
    if (!response) return empty;
    return {
      ...empty,
      ...response,
      processingBatchMemberships: Array.isArray(response.processingBatchMemberships)
        ? response.processingBatchMemberships
        : [],
    };
  } catch (error: any) {
    console.error('Error fetching loan tracking options:', error);
    return empty;
  }
};

export const fetchLoanEvents = async (
  applicationId: number
): Promise<any[]> => {
  try {
    const response = await api.get({
      path: '/v2/events',
      options: {
        where: {
          applicationId,
          claimId: null,
        },
      },
    });

    if (response) {
      return Array.isArray(response) ? response : [];
    }
    return [];
  } catch (error: any) {
    console.error('Error fetching loan events:', error);
    throw new Error(error.message || 'Failed to fetch loan events');
  }
};

export const fetchClaimEvents = async (
  claimIds: number[]
): Promise<any[]> => {
  if (claimIds.length === 0) return [];

  try {
    const response = await api.get({
      path: '/v2/events',
      options: {
        where: {
          applicationId: null,
          claimId: { in: claimIds },
        },
      },
    });

    if (response) {
      return Array.isArray(response) ? response : [];
    }
    return [];
  } catch (error: any) {
    console.error('Error fetching claim events:', error);
    throw new Error(error.message || 'Failed to fetch claim events');
  }
};

export const createEvent = async (data: {
  applicationId?: number | null;
  eventCode: string;
  status: string;
  eventDate: string;
  assignedUserId: string;
  note?: string;
  claimId?: number | null;
}): Promise<any> => {
  try {
    const body = { ...data };
    if (data.claimId != null && (data.applicationId == null || data.applicationId === undefined)) {
      body.applicationId = null;
    }
    const response = await api.post({
      path: '/v2/events',
      body,
    });

    return response;
  } catch (error: any) {
    console.error('Error creating event:', error);
    throw new Error(error.message || 'Failed to create event');
  }
};

export const updateEvent = async (
  id: number,
  data: {
    eventCode?: string;
    status?: string;
    eventDate?: string;
    assignedUserId?: string;
    note?: string;
    claimId?: number | null;
    applicationId?: number | null;
  }
): Promise<any> => {
  try {
    const response = await api.patch({
      path: `/v2/events/${id}`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error updating event:', error);
    throw new Error(error.message || 'Failed to update event');
  }
};

export const deleteEvent = async (id: number): Promise<void> => {
  try {
    await api.delete({
      path: `/v2/events/${id}`,
      body: {},
    });
  } catch (error: any) {
    console.error('Error deleting event:', error);
    throw new Error(error.message || 'Failed to delete event');
  }
};

export const fetchEdoUsers = async (): Promise<any[]> => {
  try {
    const currentUser = userAccountSignal.value.userData;
    const { PORTAL_TYPES, USER_STATUS } = $appSettings.value.constants;

    const accountId = currentUser.account?.id || (currentUser as any).accountId;
    const portalType = currentUser.account?.portalType;

    const isEdoUser = portalType === PORTAL_TYPES.edo ||
      portalType?.toUpperCase() === 'EDO' ||
      portalType?.toLowerCase() === PORTAL_TYPES.edo?.toLowerCase();

    if (isEdoUser && accountId) {
      const response = await api.get({
        path: '/v2/users',
        options: {
          where: {
            accountId: accountId,
          },
          include: {
            account: true,
          },
        },
      });

      const users = Array.isArray(response) ? response : [];

      return users.filter((user: any) => {
        const isActive =
          !user.userStatus || user.userStatus === USER_STATUS.active;
        const userAccountId = user.accountId;
        const isSameAccount = userAccountId === accountId;
        return isActive && isSameAccount;
      });
    }

    return [];
  } catch (error: any) {
    console.error('Error fetching EDO users:', error);
    return [];
  }
};

export type LenderContactOption = {
  id: number;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneAreaCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  contactRole: string | null;
  contactRoleCode: string | null;
};

/** EDO-visible lenders from GET /v2/lenders (for Application Details reassignment). */
export type RegisteredLenderOption = { id: number; name: string | null };

export const fetchRegisteredLendersForDetailsSelect = async (): Promise<RegisteredLenderOption[]> => {
  try {
    const response = await api.get({
      path: '/v2/lenders',
      options: {},
    });
    if (!Array.isArray(response)) return [];
    return response.map((l: { id: number; name?: string | null }) => ({
      id: l.id,
      name: l.name ?? null,
    }));
  } catch (error: any) {
    console.error('Error fetching lenders for application details:', error);
    return [];
  }
};

/**
 * GET /v2/lenders/:id/contacts — lender portal contacts (same access as lender show).
 */
export const fetchLenderContacts = async (
  lenderId: number
): Promise<LenderContactOption[]> => {
  try {
    const response = await api.get({
      path: `/v2/lenders/${lenderId}/contacts`,
      options: {},
    });
    return Array.isArray(response) ? response : [];
  } catch (error: any) {
    console.error('Error fetching lender contacts:', error);
    return [];
  }
};

export const fetchApplicationDetails = async (
  applicationId: number
): Promise<any> => {
  try {
    const response = await api.get({
      path: `/v2/applications/${applicationId}/details`,
      options: {},
    });

    return response;
  } catch (error: any) {
    console.error('Error fetching application details:', error);
    throw new Error(error.message || 'Failed to fetch application details');
  }
};

export const updateApplicationDetails = async (
  applicationId: number,
  data: Record<string, any>
): Promise<any> => {
  try {
    const response = await api.patch({
      path: `/v2/applications/${applicationId}/details`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error updating application details:', error);
    throw new Error(error.message || 'Failed to update application details');
  }
};

export const updateCspInfo = async (
  applicationId: number,
  cspInfoId: number,
  data: Record<string, any>
): Promise<any> => {
  try {
    const response = await api.patch({
      path: `/v2/applications/${applicationId}/csp-infos/${cspInfoId}`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error updating CspInfo:', error);
    throw new Error(error.message || 'Failed to update Collateral Support info');
  }
};

export const createCspInfo = async (
  applicationId: number,
  data: Record<string, any>
): Promise<any> => {
  try {
    const response = await api.post({
      path: `/v2/applications/${applicationId}/csp-infos`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error creating CspInfo:', error);
    throw new Error(error.message || 'Failed to create Collateral Support info');
  }
};

export const createPayment = async (
  applicationId: number,
  cspInfoId: number,
  data: {
    paymentType?: string | null;
    transactionDate?: string | null;
    generalSupportAmount?: number | null;
    sacSupportAmount?: number | null;
    fee?: number | null;
    reasonCode?: string | null;
  }
): Promise<any> => {
  try {
    const response = await api.post({
      path: `/v2/applications/${applicationId}/csp-infos/${cspInfoId}/payments`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error creating payment:', error);
    throw new Error(error.message || 'Failed to create payment');
  }
};

export const updatePayment = async (
  applicationId: number,
  cspInfoId: number,
  paymentId: number,
  data: {
    paymentType?: string | null;
    transactionDate?: string | null;
    generalSupportAmount?: number | null;
    sacSupportAmount?: number | null;
    fee?: number | null;
    reasonCode?: string | null;
  }
): Promise<any> => {
  try {
    const response = await api.patch({
      path: `/v2/applications/${applicationId}/csp-infos/${cspInfoId}/payments/${paymentId}`,
      body: data,
    });

    return response;
  } catch (error: any) {
    console.error('Error updating payment:', error);
    throw new Error(error.message || 'Failed to update payment');
  }
};

export const deletePayment = async (
  applicationId: number,
  cspInfoId: number,
  paymentId: number
): Promise<void> => {
  try {
    await api.delete({
      path: `/v2/applications/${applicationId}/csp-infos/${cspInfoId}/payments/${paymentId}`,
      body: {},
    });
  } catch (error: any) {
    console.error('Error deleting payment:', error);
    throw new Error(error.message || 'Failed to delete payment');
  }
};

/** Create a new loss claim. Amounts in body are in dollars. */
export const createLossClaim = async (
  applicationId: number,
  data: Record<string, unknown>
): Promise<any> => {
  try {
    const response = await api.post({
      path: '/v2/lossClaims',
      body: { ...data, applicationId },
    });
    return response;
  } catch (error: any) {
    console.error('Error creating loss claim:', error);
    throw new Error(error.message || 'Failed to create loss claim');
  }
};

/** Update a loss claim by id. Amounts in body are in dollars. */
export const updateLossClaim = async (
  lossClaimId: number,
  data: Record<string, unknown>
): Promise<any> => {
  try {
    const response = await api.patch({
      path: `/v2/lossClaims/${lossClaimId}`,
      body: data,
    });
    return response;
  } catch (error: any) {
    console.error('Error updating loss claim:', error);
    throw new Error(error.message || 'Failed to update loss claim');
  }
};

/** Delete a loss claim by id. */
export const deleteLossClaim = async (lossClaimId: number): Promise<void> => {
  try {
    await api.delete({
      path: `/v2/lossClaims/${lossClaimId}`,
      body: {},
    });
  } catch (error: any) {
    console.error('Error deleting loss claim:', error);
    throw new Error(error.message || 'Failed to delete loss claim');
  }
};

/**
 * Fetch Loan Tracking PDF for preview. Returns an object URL (revoke when done).
 * Uses JSON/base64 response to avoid binary response issues in Azure Functions.
 */
export const fetchLoanTrackingPdfPreviewUrl = async (
  applicationId: number
): Promise<string> => {
  const response = await api.get({
    path: `/v2/applications/${applicationId}/loanTrackingPdf/preview`,
    options: {},
  });
  // Backend wraps all res.json() in { data: body }, so we get { data: base64 } and may need .data
  const base64 = typeof response === 'string' ? response : response?.data;
  if (!base64 || typeof base64 !== 'string') {
    throw new Error('Invalid PDF preview response');
  }
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return window.URL.createObjectURL(blob);
};

/**
 * Silently refresh the notes list for the currently open application.
 * Uses the lean GET /v2/applications/:id/notes endpoint — no full app reload,
 * no loader flicker, no CSP/disbursement/claim overhead.
 */
export const fetchApplicationNotes = async (): Promise<void> => {
  const { id, modelType } = applicationDetailSignal.value;
  if (modelType !== 'application' || !id) return;
  try {
    const notes = await api.get({
      path: `/v2/applications/${id}/notes`,
      options: {},
    });
    if (Array.isArray(notes)) {
      applicationDetailSignal.update({ notes });
    }
  } catch (error: any) {
    console.error('Error refreshing application notes:', error);
  }
};

/**
 * Save the Loan Tracking PDF as an application document (generates and stores on server).
 */
export const saveLoanTrackingPdfDocument = async (
  applicationId: number
): Promise<unknown> => {
  const data = await api.post({
    path: `/v2/applications/${applicationId}/loanTrackingPdf/documents`,
    body: {},
  });
  return data;
};
